import { NATIVE_SENTINEL } from './addresses'
import { priceImpactBps } from './rank'
import type { Quote, SwapSettings, SwapToken } from './types'

/**
 * KyberSwap, asked through our own gateway.
 *
 * It exists to keep the page honest: if an aggregator can beat the routes we
 * found, the user should be told so and be able to take it. Which means this
 * module's most important property is that it cannot break the page — every
 * failure returns `null`, and the routes we found ourselves render regardless
 * of whether the gateway, or KyberSwap, is having a good day.
 */

/** KyberSwap's own sentinel for native ETH; ours is the zero address. */
export const KYBER_NATIVE = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'

export function toKyberToken(address: string): string {
  return address.toLowerCase() === NATIVE_SENTINEL ? KYBER_NATIVE : address
}

export interface KyberQuoteData {
  routeSummary: unknown
  routerAddress: string
  amountOut: bigint
}

export interface KyberBuiltTx {
  to: string
  data: string
  value: bigint
  amountOut: bigint
}

/**
 * One quote from the aggregator, or nothing.
 *
 * Deliberately swallows every failure: a `null` is one missing row in the
 * route list, while a throw here would take down a quote pipeline whose other
 * results are perfectly good. The gateway already records what went wrong on
 * its side, which is where a diagnosis belongs.
 */
export async function fetchKyberQuote(params: {
  tokenIn: SwapToken
  tokenOut: SwapToken
  amountIn: bigint
  signal?: AbortSignal
}): Promise<Quote | null> {
  const { tokenIn, tokenOut, amountIn, signal } = params
  const raw = await fetchKyberAmountOut(params)
  if (!raw) return null

  /*
   * The aggregator's row is measured like every other one.
   *
   * It arrived without a price impact at first, and the consequence was worse
   * than a blank cell: KyberSwap can win a thin pair by a hundredth of a
   * percent, and selecting it then silently switched off the warning — and the
   * confirmation gate behind it — on a trade losing fifteen per cent to the
   * pool. So the same route is asked for a thousandth of the size, and the two
   * answers give the impact by the same arithmetic as everywhere else. The
   * gateway caches for five seconds, so the second call usually costs nothing.
   */
  const referenceIn = amountIn / 1000n
  const reference =
    referenceIn > 0n
      ? await fetchKyberAmountOut({ ...params, amountIn: referenceIn })
      : null

  return {
    candidate: { venue: 'kyber' },
    amountIn,
    amountOut: raw.amountOut,
    amountOutAtReferencePrice: reference?.amountOut,
    priceImpactBps: reference
      ? priceImpactBps(amountIn, raw.amountOut, referenceIn, reference.amountOut)
      : undefined,
    kyber: { routeSummary: raw.routeSummary, routerAddress: raw.routerAddress }
  }
}

/** One `routes` call, reduced to what the page needs, or nothing. */
async function fetchKyberAmountOut(params: {
  tokenIn: SwapToken
  tokenOut: SwapToken
  amountIn: bigint
  signal?: AbortSignal
}): Promise<KyberQuoteData | null> {
  const { tokenIn, tokenOut, amountIn, signal } = params
  if (amountIn <= 0n) return null
  const query = new URLSearchParams({
    tokenIn: toKyberToken(tokenIn.address),
    tokenOut: toKyberToken(tokenOut.address),
    amountIn: amountIn.toString()
  })
  try {
    const response = await fetch(`/api/kyberswap/routes?${query.toString()}`, {
      method: 'GET',
      headers: { accept: 'application/json' },
      signal
    })
    if (!response.ok) return null
    const body = (await response.json()) as {
      data?: { routeSummary?: { amountOut?: string }; routerAddress?: string }
    }
    const summary = body?.data?.routeSummary
    const routerAddress = body?.data?.routerAddress
    if (!summary || typeof routerAddress !== 'string') return null
    const amountOut = summary.amountOut
    if (typeof amountOut !== 'string' || !/^[0-9]+$/.test(amountOut)) return null
    const out = BigInt(amountOut)
    if (out === 0n) return null
    return { routeSummary: summary, routerAddress, amountOut: out }
  } catch {
    return null
  }
}

/**
 * The transaction KyberSwap wants us to send.
 *
 * Unlike the quote this one throws, because by the time it is called the user
 * has chosen this route and pressed the button: a silent `null` there would be
 * a button that does nothing, which is worse than a sentence saying the
 * aggregator would not answer.
 */
export async function buildKyberTx(params: {
  quote: Quote
  account: string
  settings: SwapSettings
  deadline: bigint
  /** Who pays KyberSwap's router: the wallet, or our router when one is in the path. */
  sender?: string
  signal?: AbortSignal
}): Promise<KyberBuiltTx> {
  const { quote, account, settings, deadline, signal } = params
  const sender = params.sender ?? account
  if (!quote.kyber) throw new Error('buildKyberTx: this quote did not come from KyberSwap')
  const response = await fetch('/api/kyberswap/route/build', {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({
      routeSummary: quote.kyber.routeSummary,
      sender,
      recipient: account,
      slippageTolerance: settings.slippageBps,
      deadline: Number(deadline),
      source: 'mprolab'
    }),
    signal
  })
  if (!response.ok) {
    throw new Error(`KyberSwap could not build the transaction (${response.status})`)
  }
  const body = (await response.json()) as {
    data?: { data?: string; routerAddress?: string; amountOut?: string; transactionValue?: string }
  }
  const built = body?.data
  if (!built?.data || !built.routerAddress) {
    throw new Error('KyberSwap returned no transaction')
  }
  return {
    to: built.routerAddress,
    data: built.data,
    value: BigInt(built.transactionValue ?? '0'),
    amountOut: BigInt(built.amountOut ?? quote.amountOut.toString())
  }
}
